import Component from 'core/Component';
import { deepMerge } from 'toolbox/deepMerge';
import { Event } from 'services/EventEmitter';
import { stringify } from 'toolbox/queryString';
import { ajax } from 'toolbox/ajax';
import { safeGet } from 'toolbox/object';
import { on, off } from 'toolbox/event';
import { getData } from 'services/DataLayer';

/**
 * FaceBookCAPI is used to listed to different page events and send data via ajax calls
 * @class
 * @classdesc This is a description of the FaceBookCAPI class. (must be edited)
 * @extends Component
 */
export default class FaceBookCAPI extends Component {
    /**
     * Constructor of the class that mainly merge the options of the components
     * @param {HTMLElement} element HTMLElement of the component
     * @param {object} options options that belongs to the component
     */
    constructor(element, options = {}) {
        super(element, deepMerge({
            // Your custom options goes here
            qualifiedVisitTimeout: 2000, // timeout for page qualified visit event
            urls: {
                capi: '', // request url
            },
        }, options));
    }

    /**
     * All selectors must be cached. Never cache elements that are out of the component scope
     */
    initCache() {
        this.selectors.document = document;
    }

    /**
     * Init the different state of the component
     * It helps to avoid heavy DOM manipulation
     */
    initState() {
        // identify if page was scrolled. Required for qualifiedVisit event
        this.state.pageScrolled = false;
        // time to qualify visit elapsed
        this.state.qualifiedVisitTimeoutExpired = false;
    }

    /**
     * Should contain only event listeners and nothing else
     * All the event handlers should be into a separated function. No usage of anonymous function
     */
    bindEvents() {
        // need to listen to the bubbled event
        on('signup.success', this.selectors.document, this.onSignUpSuccess.bind(this));
        // track page scroll
        Event.once('page.scrolled', this.oncePageScrolled.bind(this));
        Event.on('analytics.event', this.onAddToBagEvent.bind(this));
        Event.on('analytics.event', this.onStoreLocatorEvent.bind(this));
        Event.on('analytics.event', this.onProductDetailEvent.bind(this));
        Event.on('analytics.event', this.onVTOEvent.bind(this));
    }

    /**
     * After init
     * Run any script after the component is fully initialized
     */
    afterInit() {
        this.pageViewTimer = setTimeout(() => {
            this.state.qualifiedVisitTimeoutExpired = true;
            this.checkQualifiedVisit();
        }, this.options.qualifiedVisitTimeout);

        // check "server" triggered analytic events
        this.checkCheckoutEvents();
        this.checkPurchaseEvent();
    }

    /**
     * Newsletter sign up success handler
     * @param {object} data - data object
     */
    onSignUpSuccess(data) {
        if (safeGet(data, 'detail.content.success')) {
            this.sendRequest({
                eventName: 'subscription',
            });
        }
    }

    /**
     * pageScrolled event handler
     * mark page as scrolled, this is required for qualified visit event
     */
    oncePageScrolled() {
        this.state.pageScrolled = true;
        this.checkQualifiedVisit();
    }

    /**
     * determine if qualified visit event should be triggered
     */
    checkQualifiedVisit() {
        if (this.state.pageScrolled && this.state.qualifiedVisitTimeoutExpired) {
            this.sendRequest({
                eventName: 'qualifiedVisit',
            });
        }
    }

    /**
     * check if checkout analytics events should be triggered
     */
    checkCheckoutEvents() {
        const data = getData();

        const checkoutEvents = safeGet(data, 'events', []);

        const fisrtStepEvents = checkoutEvents.filter((event) => {
            const step = safeGet(event, 'ecommerce.checkout.actionField.step');
            return step === 1;
        });

        // initialize checkout event
        if (fisrtStepEvents.length && safeGet(fisrtStepEvents[0], 'ecommerce.checkout.products', []).length) {
            this.sendRequest({
                eventName: 'initiateCheckout',
            });
        }
    }

    /**
     * Determine Purchase event should be triggered
     */
    checkPurchaseEvent() {
        const data = getData();

        // purchase event
        if (data) {
            const orderConfirmationPageRegExp = new RegExp('confirmation|orderconfirmation|ngorderconfirmation');
            const orderConfirmationPageId = safeGet(data, 'page.id');

            // if order confirmation page is rendered, send purchase analytics
            if (orderConfirmationPageRegExp.test(orderConfirmationPageId)) {
                const orderNumber = safeGet(data, 'orderData.orderNo');

                if (orderNumber) {
                    this.sendRequest({
                        eventName: 'purchase',
                        data: JSON.stringify({
                            orderNumber,
                        }),
                    });
                }
            }
        }
    }

    /**
     * VTO analytics event handler
     * @param {object} data - event data
     */
    onVTOEvent(data) {
        const vtoCategory = new RegExp('virtual try on');
        const vtoAction = new RegExp('select');
        const vtoLabel = new RegExp('upload a photo|live camera');

        // vto event
        if (vtoCategory.test(data.category) && vtoAction.test(data.action) && vtoLabel.test(data.label)) {
            this.sendRequest({
                eventName: 'vto',
            });
        }
    }

    /**
     * Returns default product params
     * @param {Object} product product object
     * @return {Object} product params data
     */
    getDefaultProductParams(product) {
        return {
            productID: product.id,
            productName: encodeURIComponent(product.name),
            quantity: product.quantity,
            productPrice: product.price,
        };
    }

    /**
     * Product Details analytics event handler
     * @param {object} data - event data
     */
    onProductDetailEvent(data) {
        const viewContentRegExp = new RegExp('Product Detail');

        // view content event
        if (viewContentRegExp.test(data.eventAction)) {
            const products = safeGet(data, 'ecommerce.detail.products', []).map(product => this.getDefaultProductParams(product));

            if (products.length) {
                this.sendRequest({
                    eventName: 'viewContent',
                    data: JSON.stringify(products[0]),
                });
            }
        }
    }

    /**
     * StoreLocator analytics event handler
     * @param {object} data - event data
     */
    onStoreLocatorEvent(data) {
        const storeLocatorRegExp = new RegExp('store locator|salon locator');

        // store locator event
        if (storeLocatorRegExp.test(data.category)) {
            this.sendRequest({
                eventName: 'findLocation',
            });
        }
    }

    /**
     * AddToBag analytics event handler
     * @param {object} data - event data
     */
    onAddToBagEvent(data) {
        const addToBagRegExp = new RegExp('Add to Cart');

        // add to bag event
        if (addToBagRegExp.test(data.eventAction)) {
            const products = safeGet(data, 'ecommerce.add.products', []).map(product => this.getDefaultProductParams(product));

            if (products.length) {
                this.sendRequest({
                    eventName: 'addToCart',
                    data: JSON.stringify(products),
                });
            }
        }
    }

    /**
     * Send request via ajax call
     * @param {object} requestData - data to send
     * @param {string} requestData.eventName - event name
     * @param {string} requestData.data - data to send in json format
     */
    sendRequest(requestData) {
        // exit if there is no event name
        if (!requestData || !requestData.eventName) {
            return;
        }
        // add empty data object to make request object consistent
        if (!requestData.data) {
            requestData.data = {};
        }
        ajax(this.options.urls.capi, {
            method: 'POST',
            body: stringify(requestData),
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        });
    }

    /**
     * Destroy is called automatically after the component is being removed from the DOM
     * You must always destroy the listeners attached to an element to avoid any memory leaks
     */
    destroy() {
        off('signup.success', this.selectors.document);
        Event.removeListener('analytics.event');
        Event.removeListener('page.scrolled');
    }
}
